import { execAdmin } from '@src/apis/reware/utils';

/**
 * Raw shape of the admin schema's `PartnerAdmin.vitalElements` JSONB column — the DB columns
 * that compose a partner's SKU identity, grouped by entity (e.g. partner: parent_sku, color_code,
 * size_code). Only the `partner` group matters for catalog uploads. Unrelated to
 * `src/vitalElements` despite the shared name — that's a different, public-schema concept (facet
 * tags like "color"/"size" a partner has chosen as important product properties).
 */
interface PartnerVitalElements {
  partner?: string[];
}

interface PartnerVitalElementsResponse {
  partner: {
    vitalElements: PartnerVitalElements | null;
  } | null;
}

interface PartnerVitalElementsVariables {
  code: string;
}

const QUERY = `
  query catalogUploadTemplateVitalElements($code: String!) {
    partner(code: $code) {
      vitalElements
    }
  }
`;

/**
 * Fetches the DB column names (e.g. `parent_sku`, `color_code`, `size_code`) that compose this
 * partner's SKU identity — used to exclude those columns from the "Fields to Update" step on an
 * update template, since they identify the record rather than being updatable data. Gated by the
 * `admin_vital_elements_portal` permission; a session without it gets a GraphQL error back, so
 * callers should treat a thrown error as "nothing to exclude" rather than surface it.
 */
export async function fetchPartnerVitalElementColumns(partnerCode: string): Promise<string[]> {
  const data = await execAdmin<PartnerVitalElementsVariables, PartnerVitalElementsResponse>(
    QUERY,
    { variables: { code: partnerCode } },
  );
  return data.partner?.vitalElements?.partner ?? [];
}
